import * as AnalysisActions from '@modules/analysis/_store/actions/analysis.action';
import { Action, createReducer, on } from '@ngrx/store';
import { AnalysisDocument, AnalysisStateData } from 'src/app/shared/interface/analysis';

export const initialState: AnalysisStateData = {
  info: null,
  documents: null,
  loaded: false,
  loading: false,
  error: null,
  reasons: []
};

const loadAnalysisReducer = createReducer(
  initialState,
  on(AnalysisActions.timeoutAnalysisSuccess, () => initialState),
  on(AnalysisActions.resetState, (state) => ({ ...initialState })),
  on(AnalysisActions.loadAnalysis, (state) => ({ ...state })),
  on(AnalysisActions.loadAnalysisFail, (state, { error }) => ({
    ...state,
    loaded: false,
    loading: false,
    error
  })),
  on(AnalysisActions.loadAnalysisSuccess, (state, { payload }) => {
    const { info, documents, reasons } = payload;
    const {
      events: { analysis_module },
      events,
      ...rest
    } = info;

    const newDocuments = updateAllStatusDocument(documents);

    return {
      ...state,
      info: {
        analysis_module,
        candidateName: (events["candidate/info-pessoal/nome"].value || ''),
        ...rest,
      },
      documents: newDocuments,
      reasons,
      configV4json: events,
      loaded: true,
      loading: false,
    };
  }),
  on(AnalysisActions.selectDocument, (state, { module: _module, payload }) => {
    const document = payload?.data?.document;
    const entities = Object.keys(state.documents);

    let newState;
    const documents = entities.reduce((all, item) => {
      if (item === document) {
        const updatedDocument = updateDocumentStatus({ ...state.documents[item], selected: true });

        newState = {
          ...newState,
          [item]: updatedDocument,
        };
      } else {
        newState = {
          ...newState,
          [item]: { ...state.documents[item], selected: false },
        };
      }
      return newState;
    }, {});
    return {
      ...state,
      documents,
    };
  }),
  on(AnalysisActions.updateDocuments, (state, { documents }) => ({
    ...state,
    documents,
  })),
  on(AnalysisActions.setCurrentVoucher, (state, { module: _module, payload, documentKey: _documentKey, document }) => {
    const voucherKey = payload.data.image;
    let updatableImages = findReferences(state, voucherKey, document.key, 'images');
    // Update all images with the new status
    updatableImages.forEach(image => {
      let stateDocument = state.documents[image.documentKey];

      const status = image.status === 'reproved' ? image.status : 'approved';

      const updatedDocumentImages = updateDocumentImage(stateDocument, image.key, status);

      const updatedDocument = updateDocumentStatus({
        ...stateDocument,
        images: {
          ...stateDocument.images,
          selected: voucherKey,
          data: updatedDocumentImages
        }
      })

      state = updateStateDocument(state, image.documentKey, updatedDocument)
    })
    return state;
  }
  ),
  on(
    AnalysisActions.approveVoucherSuccess,
    (state, { key, document }) => {
      let updatableImages = findReferences(state, key, document.key, 'images');
      // Update all images with the new status
      updatableImages.forEach(image => {
        let stateDocument = state.documents[image.documentKey];

        const updatedDocumentImages = updateDocumentImage(stateDocument, image.key, 'approved');

        const updatedDocument = updateDocumentStatus({
          ...stateDocument,
          images: {
            ...stateDocument.images,
            data: updatedDocumentImages
          }
        })

        state = updateStateDocument(state, image.documentKey, updatedDocument)
      })
      return state;
    }
  ),
  // this status change is triggered for every field change/approval in the analysis.
  on(
    AnalysisActions.updateFieldSuccess,
    (state, { documentKey, payload }) => {
      const { key, kind, data: { value } } = payload[0];
      // status always starts with approved as default if there are any changes this field will appear as not validated
      let status: string = 'approved';
      // Validation to understand if that field was unchecked
      if (kind === 'field_allow_change') {
        status = 'not_validated';
      }

      // childFieldKey is the key of a child field
      let childFieldKey = getFieldChild(key);

      if (childFieldKey && kind !== 'field_approved') {
        // Find all places where the target field is being referenced
        let updatableChildFields = findReferences(state, childFieldKey, documentKey, 'fields');

        // Update all fields with the new status
        updatableChildFields.forEach(field => {
          let stateDocument = state.documents[field.documentKey];

          const updatedDocumentFields = updateDocumentField(stateDocument, 'field_allow_change', field.key, 'not_validated', field.value);

          const updatedDocument = updateDocumentStatus({
            ...stateDocument,
            fields: {
              ...stateDocument.fields,
              data: updatedDocumentFields
            }
          })

          state = updateStateDocument(state, field.documentKey, updatedDocument)
        })
      }

      // Find all places where the target field is being referenced
      let updatableFields = findReferences(state, key, documentKey, 'fields');
      // Update all fields with the new status
      updatableFields.forEach(field => {
        let stateDocument = state.documents[field.documentKey];

        const updatedDocumentFields = updateDocumentField(stateDocument, kind, field.key, status, value);

        const updatedDocument = updateDocumentStatus({
          ...stateDocument,
          fields: {
            ...stateDocument.fields,
            data: updatedDocumentFields
          }
        })

        state = updateStateDocument(state, field.documentKey, updatedDocument)
      })
      return state;
    }
  ),
  on(AnalysisActions.updateFieldFail, (state, { error, documentKey, payload }) => {
    const { key, kind } = payload[0];
    const status: string = 'not_validated';

    // Find all places where the target field is being referenced
    let updatableFields = findReferences(state, key, documentKey, 'fields');

    // Update all fields with the new status
    updatableFields.forEach(field => {
      let stateDocument = state.documents[field.documentKey];

      const updatedDocumentFields = updateDocumentField(stateDocument, kind, field.key, status, field.value);

      const updatedDocument = updateDocumentStatus({
        ...stateDocument,
        status: 'pending',
        fields: {
          ...stateDocument.fields,
          data: updatedDocumentFields
        }
      })

      state = updateStateDocument(state, field.documentKey, updatedDocument, error);
    })
    return state;
  }),
  on(AnalysisActions.setCurrentVoucherFail, (state, { error }) => {
    return {
      ...state,
      error: error,
    }
  }),
  on(AnalysisActions.approveVoucherFail, (state, { error }) => {
    return {
      ...state,
      error: error,
    }
  }),
  on(AnalysisActions.selectDocumentFail, (state, { error }) => {
    return {
      ...state,
      error: error,
    }
  }),
  on(
    AnalysisActions.reproveVoucherSuccess,
    (state, { documentKey, document, target, reasons }) => {


      let updatableImages = findReferences(state, target, document.key, 'images');
      // Update all images with the new status
      updatableImages.forEach(image => {
        let stateDocument = state.documents[image.documentKey];

        const updatedDocumentImages = updateDocumentImage(stateDocument, image.key, 'reproved', reasons);

        const updatedDocument = updateDocumentStatus({
          ...stateDocument,
          images: {
            ...stateDocument.images,
            data: updatedDocumentImages
          }
        })

        state = updateStateDocument(state, image.documentKey, updatedDocument)
      })

      return state;
    }
  ),
  on(AnalysisActions.approveEsocialSuccess, (state, { document, status, mensage }) => ({
    ...state,
    documents: {
      ...state.documents,
      esocial_module: {
        ...state.documents.esocial_module,
        reasons: {
          ...state.documents.esocial_module?.reasons,
          selected: document?.data?.reasons
        },
        mensage: mensage,
        icon: status,
        status,
      },
    },
  }))
);

// updateDocumentField updates the status of a field with a given fieldKey in a document and returns an updated list of fields
export function updateDocumentField(document: AnalysisDocument, eventKind: string, fieldKey: string, status: string, value: string) {
  const updatedFields = document.fields.data.map((field) => {
    const fieldHasSameKey = field.key === fieldKey;
    if (eventKind !== 'field_change') {
      return fieldHasSameKey ? { ...field, status } : field;
    }
    return fieldHasSameKey ? { ...field, status, value } : field;
  });
  return updatedFields;
}

// updateDocumentImage updates the status of a image with a given imagekey in a document and returns an updated list of images
export function updateDocumentImage(document: AnalysisDocument, imageKey: string, status: string, selected = []) {
  const updatedImages = document.images?.data.map((image) => {
    const imgHasSameKey = image.key === imageKey;

    if (imgHasSameKey && image?.reasons?.last_selected.length > 0) {
      return { ...image, status, viewed: true }
    }

    if (imgHasSameKey && status !== 'reproved') {
      return { ...image, reasons: { selected, note: '', last_selected: [], list: image?.reasons?.list }, status, viewed: true }
    }

    if (!imgHasSameKey) {
      return image;
    }

    if (imgHasSameKey && status === 'reproved') {
      return selected.length > 0 ? { ...image, reasons: { selected, note: '', last_selected: [], list: image?.reasons?.list }, status, viewed: true } : { ...image, status, viewed: true }
    }

  });
  return updatedImages;
}

// Reference is a reference to a field or images, and points to a key in a document with a documentKey
export class Reference {
  key: string;
  documentKey: string;
  status: string;
  value: string;
}

// findReferences returns the references to the documents, fields and images where a property with a given key is being referenced
// data receives values fields and images
export function findReferences(state, referenceKey: string, documentKey: string, data: string): Reference[] {
  let mirroredFields = [];

  Object.keys(state.documents).map(((key) => {
    state.documents[key]?.[data]?.data?.forEach(reference => {
      if (reference.key === referenceKey) {
        mirroredFields.push({
          key: referenceKey,
          documentKey: key,
          status: reference.status,
          value: reference.value,
        })
      }
    });
  }));

  return mirroredFields;
}

export function reducer(state: AnalysisStateData | undefined, action: Action) {
  return loadAnalysisReducer(state, action);
}

export const getAnalysis = (state: AnalysisStateData) => state;
export const getAnalysisLoading = (state: AnalysisStateData) => state.loading;
export const getAnalysisLoaded = (state: AnalysisStateData) => state.loaded;
export const getAnalysisDocuments = (state: AnalysisStateData) =>
  state.documents;

export const getConfig = (state: AnalysisStateData) => state.configV4json;
export const getInfos = (state: AnalysisStateData) => state.info;
export const getAnalysisReasons = (state: AnalysisStateData) => state?.reasons;
export const getCandidateName = (state: AnalysisStateData) => state.info.candidateName;

export function updateDocumentStatus(document: AnalysisDocument): AnalysisDocument {
  for (let i = 0; i < document.images.data.length; i++) {
    const image = document.images.data[i];

    if (image.status === 'reproved') {
      document.status = 'reproved';
      return document;
    }

    if (image.status !== 'approved') {
      document.status = 'pending';
      return document;
    }
  }

  if (document.fields.data.length > 0 &&
    document.fields.data.length === document.fields.references.length) {
    document.status = 'approved';
    return document;
  }

  for (let i = 0; i < document.fields.data.length; i++) {
    const field = document.fields.data[i];

    if (field.status !== 'approved' && field.type !== 'none') {
      document.status = 'pending';
      return document;
    }
  }

  document.status = 'approved';
  return document;
}

// updateStateDocument updates a document with a given documentKey in the state, setting it with new data
export function updateStateDocument(state, documentKey: string, data: AnalysisDocument, error = null): any {
  return {
    ...state,
    documents: {
      ...state.documents,
      [documentKey]: data,
    },
    error: error,
  }
}


// updateStatusImage updates images that are not in the approved status but have already been viewed
export function updateStatusImage(documents: any) {
  const entities = Object.keys(documents);
  let updatedDocument = { ...documents }
  for (let i = 0; i < entities.length; i++) {
    const key = entities[i];
    if (documents[key]?.images?.data) {
      const updatedImages = documents[key].images.data.map((data) => {
        if (data.status !== 'reproved' && data.viewed) {
          return { ...data, status: 'approved' }
        }
        if (data?.deleted || data?.dismissed) {
          return { ...data, status: 'approved' }
        }
        return data;
      });
      updatedDocument = updateImageData(updatedDocument, key, updatedImages);
    }
  }
  return updatedDocument;
}

// updateAllStatusDocument updates images and field that are not in the approved status but have already been viewed and approved
export function updateAllStatusDocument(documents: any) {
  const entities = Object.keys(documents);
  let updatedDocument = { ...documents }
  for (let i = 0; i < entities.length; i++) {
    const key = entities[i];
    if (documents[key]?.images?.data) {
      const updatedImages = documents[key].images.data.map((data) => {
        if (data.status !== 'reproved' && data.viewed) {
          return { ...data, status: 'approved' }
        }
        if (data?.deleted || data?.dismissed) {
          return { ...data, status: 'approved' }
        }
        return data;
      });
      updatedDocument = updateImageData(updatedDocument, key, updatedImages);
    }
  }
  const newEntities = Object.keys(updatedDocument);
  let updatedFields = { ...updatedDocument }
  for (let i = 0; i < newEntities.length; i++) {
    const key = newEntities[i];
    if (updatedDocument[key]?.fields?.data) {
      const hasAllFieldApproved = updatedDocument[key]?.fields?.data?.every((data) => data?.status === 'approved');
      const hasAllImageApproved = updatedDocument[key]?.images?.data?.every((data) => data?.status === 'approved');
      if (hasAllFieldApproved && hasAllImageApproved) {
        updatedFields = updateDocumentData(updatedFields, key, 'approved');
      }
      if (hasAllImageApproved && updatedDocument[key]?.fields?.data?.length === updatedDocument[key]?.fields?.references?.length) {
        updatedFields = updateDocumentData(updatedFields, key, 'approved');
      }
    }
  }
  return updatedFields;
}

// updateImageData updates the data that is in the images object
export function updateImageData(state: any, key: string, updatedImages: any) {
  return {
    ...state,
    [key]: {
      ...state[key],
      images: {
        ...state[key].images,
        data: updatedImages
      }
    },
    error: null,
  };
}

export function updateDocumentData(state: any, key: string, status: any) {
  return {
    ...state,
    [key]: {
      ...state[key],
      status: status
    },
    error: null,
  };
}

// getFieldChild fetches fields that have parent/child relationships
export function getFieldChild(field_key: string): string {
  switch (field_key) {
    case "candidate/rg/naturalidadeEstado":
      return "candidate/rg/naturalidadeCidade";
    case "candidate/titulo-eleitor/uf":
      return "candidate/titulo-eleitor/municipio";
    case "candidate/contrato-estagio/uf":
      return "candidate/contrato-estagio/cidade";
    case "candidate/endereco/uf":
      return "candidate/endereco/cidade";
  }
  return ""
}
